import type {
  EngineDocument,
  SceneNode,
  SceneCanvasState,
} from "../scene/model";
import { isBlockPart, reflowBlocks } from "./blocks";
import { SceneHistory } from "../history/scene-history";
import { restoreNativeHistory } from "./history-restore";
import { assertNativeDocument, emptyNativeDocument } from "./document";
import { selectable } from "./geometry";
import { activePage, replaceActivePage, renumberPages } from "./pages";

/** Owns the live scene. Neither renderer instances nor DOM enter this session. */
export class NativeSession {
  document = emptyNativeDocument();
  readonly history = new SceneHistory();
  selectedIds: string[] = [];
  private selectionStart: string[] = [];
  private layerAnchor: string | null = null;
  dirty = false;
  revision = 0;
  generation = 0;
  private start: EngineDocument | null = null;
  constructor(readonly mode: "graphics" | "interactive" = "graphics") {
    if (mode === "interactive")
      this.document.extensions = {
        ...this.document.extensions,
        nativeMode: "interactive",
        projectId: crypto.randomUUID(),
      };
    if (mode === "interactive") {
      this.document.pages[0]!.name = "Стартовый экран";
      this.document.pages[0]!.exportName = "index";
    }
    this.reset(this.document);
  }
  get page() {
    return activePage(this.document);
  }
  get selected() {
    return this.selectedNodes.length === 1 ? this.selectedNodes[0] : undefined;
  }
  get selectedId() {
    return this.selectedIds.length === 1 ? this.selectedIds[0]! : null;
  }
  set selectedId(id: string | null) {
    this.select(id ? [id] : []);
  }
  get selectedNodes() {
    return this.page.nodes.filter(
      (n) => this.selectedIds.includes(n.id) && selectable(n),
    );
  }
  select(ids: readonly string[]) {
    const blockGroups = new Set(
      this.page.nodes
        .filter((n) => ids.includes(n.id) && isBlockPart(n))
        .map((n) => n.layer.groupId),
    );
    ids = [
      ...ids,
      ...this.page.nodes
        .filter((n) => isBlockPart(n) && blockGroups.has(n.layer.groupId))
        .map((n) => n.id),
    ];
    const available = new Set(
      this.page.nodes.filter(selectable).map((n) => n.id),
    );
    this.selectedIds = [...new Set(ids)].filter((id) => available.has(id));
  }
  selectLayer(id: string, toggle: boolean, range: boolean) {
    const order = this.page.nodes
      .slice()
      .reverse()
      .filter(selectable)
      .map((n) => n.id);
    if (!order.includes(id)) return;
    const anchor = this.layerAnchor ? order.indexOf(this.layerAnchor) : -1;
    if (range && anchor >= 0) {
      const target = order.indexOf(id);
      this.select(
        order.slice(Math.min(anchor, target), Math.max(anchor, target) + 1),
      );
    } else {
      this.select(
        toggle
          ? this.selectedIds.includes(id)
            ? this.selectedIds.filter((key) => {
                const target = this.page.nodes.find((n) => n.id === id)!;
                return (
                  key !== id &&
                  (!isBlockPart(target) ||
                    this.page.nodes.find((n) => n.id === key)?.layer.groupId !==
                      target.layer.groupId)
                );
              })
            : [...this.selectedIds, id]
          : [id],
      );
      this.layerAnchor = id;
    }
  }
  get gesturing() {
    return this.start !== null;
  }
  private state(): SceneCanvasState {
    return {
      schemaVersion: 1,
      size: this.page.size,
      pageSettings: {
        unit: this.page.unit,
        workspaceColor: this.page.workspaceColor,
        backgroundVisible: this.page.backgroundVisible,
      },
      nodes: this.page.nodes,
      assets: this.document.assets,
      compatibility: {},
    };
  }
  reset(document: EngineDocument) {
    assertNativeDocument(document);
    if (
      this.mode === "graphics" &&
      document.extensions.nativeMode === "interactive"
    )
      throw new Error(
        "Откройте этот проект в интерактивном режиме Creator Engine Pro.",
      );
    if (
      this.mode === "interactive" &&
      document.extensions.nativeMode !== "interactive"
    )
      document = {
        ...document,
        extensions: {
          ...document.extensions,
          nativeMode: "interactive",
          projectId: crypto.randomUUID(),
        },
      };
    if (this.mode === "interactive") document = renumberPages(document);
    this.document = structuredClone(document);
    this.selectedId = null;
    this.layerAnchor = null;
    this.start = null;
    this.dirty = false;
    this.revision++;
    this.generation++;
    this.history.reset(this.state());
  }
  begin() {
    if (!this.start) {
      this.start = this.document;
      this.selectionStart = this.selectedIds.slice();
    }
  }
  replaceNodes(nodes: SceneNode[]) {
    nodes = reflowBlocks(nodes, this.page.nodes);
    this.document = replaceActivePage(this.document, { ...this.page, nodes });
  }
  replaceNode(node: SceneNode) {
    this.replaceMany([node]);
  }
  replaceMany(nodes: readonly SceneNode[]) {
    const changes = new Map(nodes.map((n) => [n.id, n]));
    this.replaceNodes(this.page.nodes.map((n) => changes.get(n.id) ?? n));
  }
  finish() {
    this.start = null;
    this.reconcileSelection();
    if (this.history.commit(this.history.capture(this.state()))) {
      this.dirty = true;
      this.revision++;
    }
  }
  cancel() {
    if (this.start) {
      this.document = this.start;
      this.selectedIds = this.selectionStart;
    }
    this.start = null;
    this.reconcileSelection();
  }
  undo(redo = false) {
    if (this.gesturing) {
      this.cancel();
      return;
    }
    const snapshot = redo ? this.history.redo() : this.history.undo();
    if (!snapshot) return;
    const state = restoreNativeHistory(
      this.state(),
      this.history.read(snapshot),
    );
    this.document = replaceActivePage(
      { ...this.document, assets: state.assets },
      {
        ...this.page,
        ...state.pageSettings,
        nodes: state.nodes,
        size: state.size,
      },
    );
    this.dirty = true;
    this.revision++;
    this.reconcileSelection();
  }
  removeSelected() {
    if (!this.selectedNodes.length || this.gesturing) return;
    this.replaceNodes(
      this.page.nodes.filter((n) => !this.selectedIds.includes(n.id)),
    );
    this.selectedId = null;
    this.finish();
  }
  /** Screen transitions invalidate pending operations and reset page-local history. */
  changePages(document: EngineDocument, edited: boolean) {
    assertNativeDocument(document);
    this.cancel();
    this.document = document;
    this.selectedIds = [];
    this.layerAnchor = null;
    this.revision++;
    this.dirty = this.dirty || edited;
    this.history.reset(this.state());
  }
  private reconcileSelection() {
    this.select(this.selectedIds);
  }
}
